import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { MessageCircle, X, Send, Sparkles, Eraser } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import './AssistantWidget.css';

/*
 * Floating assistant widget.
 *
 * This is a second surface onto the SAME backend the Assistant page uses
 * (supabase edge function `intelligence-chat-v2`). The request shape, headers
 * and response fields below are a deliberate mirror of
 * src/pages/Assistant.js — do not diverge, or the widget will start giving
 * different answers to the same question and will lose the org scoping /
 * row-level security the function derives from the caller's access token.
 */

// Same constants the Assistant page posts with. The edge function is invoked
// with the signed-in user's access token so it resolves the same org + RLS.

// Assistant.js sends the last 6 turns as conversation_history; keep that.
const HISTORY_TURNS = 6;
// A pathological reply shouldn't be able to lock up the markdown renderer.
const MAX_RENDERED_CHARS = 20000;

const GENERIC_ERROR = "Sorry — I couldn't get an answer just then. Please try again in a moment.";

// react-markdown builds React nodes; no HTML from the model is ever parsed or
// injected (no rehype-raw, no dangerouslySetInnerHTML anywhere in this file).
const MD_COMPONENTS = {
  a: ({ node, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer nofollow" />
  ),
};

const AssistantWidget = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const launcherRef = useRef(null);
  const messagesRef = useRef(null);
  const xhrRef = useRef(null);
  // Guards a rapid double-send: state updates are async, this is not.
  const inFlightRef = useRef(false);

  // The Assistant page is itself a full-screen version of this chat — showing
  // the bubble on top of it is just noise.
  const hidden = location.pathname.startsWith('/assistant');

  const close = useCallback(() => {
    setOpen(false);
    launcherRef.current?.focus();
  }, []);

  // Escape closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Click outside closes.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  // Focus the input when the panel opens.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [open]);

  // Keep the log pinned to the newest message.
  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading, open]);

  // Grow the textarea with its content, up to the CSS max-height (then it
  // scrolls). Reset to auto first so it can shrink again on delete.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input, open]);

  // Don't leave a request hanging if the shell unmounts (logout, etc.).
  useEffect(() => () => { try { xhrRef.current?.abort(); } catch (e) { /* noop */ } }, []);

  const sendMessage = useCallback(async () => {
    const msg = input.trim();
    if (!msg || inFlightRef.current) return;

    if (!organizationId) {
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: msg },
        { role: 'error', content: "I can't reach your workspace data yet. Try reloading the page." },
      ]);
      setInput('');
      return;
    }

    inFlightRef.current = true;

    const userMsg = { role: 'user', content: msg };
    // Only real chat turns go back to the model — error bubbles are UI-only.
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      const session = (await supabase.auth.getSession()).data.session;
      const history = nextMessages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-HISTORY_TURNS)
        .map((m) => ({ role: m.role, content: m.content }));

      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;
      const data = await new Promise((resolve, reject) => {
        xhr.open('POST', `${SUPABASE_URL}/functions/v1/intelligence-chat-v2`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${session?.access_token}`);
        xhr.setRequestHeader('apikey', SUPABASE_ANON_KEY);
        xhr.timeout = 120000;
        xhr.onload = () => {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error('Invalid response')); }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.onabort = () => reject(new Error('Aborted'));
        xhr.ontimeout = () => reject(new Error('Request timed out'));
        xhr.send(JSON.stringify({
          message: msg,
          organization_id: organizationId,
          conversation_history: history,
        }));
      });

      if (data?.error) throw new Error(data.error);
      const reply = typeof data?.response === 'string' ? data.response.trim() : '';
      if (!reply) throw new Error('Empty response');

      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: reply,
        data_sources: Array.isArray(data.data_sources) ? data.data_sources : [],
      }]);
    } catch (err) {
      // Never surface the raw error: it can contain function names, SQL or
      // stack frames. Log for developers, show one friendly line to the user.
      console.error('Assistant widget request failed:', err);
      setMessages((prev) => [...prev, { role: 'error', content: GENERIC_ERROR }]);
    } finally {
      xhrRef.current = null;
      inFlightRef.current = false;
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, messages, organizationId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (hidden) return null;

  return (
    <div
      ref={rootRef}
      className="tt-aw-root"
      data-open={open ? 'true' : 'false'}
      data-theme={isDark ? 'dark' : 'light'}
    >
      {/* ---------------------------------------------------------- panel -- */}
      <div className="tt-aw-panel-wrap">
        <span className="tt-aw-glow" aria-hidden="true" />
        <div
          className="tt-aw-panel"
          role="dialog"
          aria-label="TutorTracer assistant"
          aria-hidden={open ? undefined : 'true'}
        >
          <div className="tt-aw-header">
            <div className="tt-aw-title">
              <span className="tt-aw-title-dot">
                <Sparkles size={12} strokeWidth={2} />
              </span>
              Assistant
            </div>
            <div className="tt-aw-header-actions">
              {messages.length > 0 && (
                <button
                  type="button"
                  className="tt-aw-icon-btn"
                  onClick={() => setMessages([])}
                  aria-label="Clear conversation"
                  title="Clear conversation"
                  tabIndex={open ? 0 : -1}
                >
                  <Eraser size={15} strokeWidth={1.75} />
                </button>
              )}
              <button
                type="button"
                className="tt-aw-icon-btn"
                onClick={close}
                aria-label="Close assistant"
                title="Close"
                tabIndex={open ? 0 : -1}
              >
                <X size={16} strokeWidth={1.75} />
              </button>
            </div>
          </div>

          <div className="tt-aw-messages" ref={messagesRef}>
            {messages.length === 0 && !loading ? (
              <div className="tt-aw-empty">
                <p className="tt-aw-empty-title">Ask me anything</p>
                <p className="tt-aw-empty-body">
                  Questions about your lessons, tutors, students, flagged issues or
                  policies — try &ldquo;how did this week go?&rdquo;
                </p>
              </div>
            ) : (
              messages.map((m, i) => {
                if (m.role === 'user') {
                  return (
                    <div key={i} className="tt-aw-msg tt-aw-msg-user">{m.content}</div>
                  );
                }
                if (m.role === 'error') {
                  return (
                    <div key={i} className="tt-aw-msg tt-aw-msg-error" role="alert">{m.content}</div>
                  );
                }
                const body = m.content.length > MAX_RENDERED_CHARS
                  ? `${m.content.slice(0, MAX_RENDERED_CHARS)}\n\n_(reply truncated — open the Assistant page for the full answer)_`
                  : m.content;
                return (
                  <div key={i} className="tt-aw-msg tt-aw-msg-bot">
                    <div className="tt-aw-md">
                      <ReactMarkdown components={MD_COMPONENTS}>{body}</ReactMarkdown>
                    </div>
                    {m.data_sources?.length > 0 && (
                      <div className="tt-aw-sources">Based on: {m.data_sources.join(' · ')}</div>
                    )}
                  </div>
                );
              })
            )}

            {loading && (
              <div className="tt-aw-msg-pending" aria-live="polite">
                <span className="tt-aw-spin" aria-hidden="true" />
                Thinking&hellip;
              </div>
            )}
          </div>

          <form
            className="tt-aw-form"
            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
          >
            <div className="tt-aw-pill">
              <textarea
                ref={inputRef}
                className="tt-aw-field"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything…"
                aria-label="Ask the assistant a question"
                disabled={loading}
                tabIndex={open ? 0 : -1}
              />
              <button
                type="submit"
                className="tt-aw-send"
                disabled={!input.trim() || loading}
                aria-label="Send message"
                tabIndex={open ? 0 : -1}
              >
                <Send size={15} strokeWidth={2} />
              </button>
            </div>
            <p className="tt-aw-hint">Enter to send · Shift+Enter for a new line</p>
          </form>
        </div>
      </div>

      {/* ------------------------------------------------------- launcher -- */}
      <button
        ref={launcherRef}
        type="button"
        className="tt-aw-launcher"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close assistant' : 'Open assistant'}
        aria-expanded={open}
        title={open ? 'Close assistant' : 'Ask the assistant'}
      >
        <span className="tt-aw-glow" aria-hidden="true" />
        {open
          ? <X size={21} strokeWidth={1.75} />
          : <MessageCircle size={21} strokeWidth={1.75} />}
      </button>
    </div>
  );
};

export default AssistantWidget;
